import React, { useState } from 'react';
import ImageUploading from 'react-images-uploading';
import { Upload, Trash2, ArrowUp, ArrowDown, ImagePlus } from 'lucide-react';

import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { useConfirm } from '../ui/ConfirmDialog';
import { useToast } from '../ui/Toast';
import { sortedPhotos } from '../../utils/photos';
import { cloudinaryThumb, cloudinaryThumbSrcSet } from '../../utils/cloudinary';
import { addProductPhotos, removeProductPhoto, reorderProductPhotos } from '../../hooks/useAdminProducts';
import { parseApiError } from '../../utils/apiError';

const MAX_PHOTOS = 10;

// Manages a product's Cloudinary photos: upload new ones (multipart 'photos'),
// remove, and reorder (PUT photoOrder). Photos are already-persisted server
// assets; `onChanged(updatedProduct)` lets the parent refresh its copy.
export default function PhotoManager({ productId, photos, onChanged }) {
  const { addToast } = useToast();
  const { confirm, dialog } = useConfirm();
  const [staged, setStaged] = useState([]); // react-images-uploading value
  const [busy, setBusy] = useState(false);

  const ordered = sortedPhotos(photos);
  const remaining = Math.max(0, MAX_PHOTOS - ordered.length);

  const upload = async () => {
    if (!staged.length) return;
    setBusy(true);
    try {
      const files = staged.map((s) => s.file).filter(Boolean);
      const updated = await addProductPhotos(productId, files);
      setStaged([]);
      onChanged?.(updated);
      addToast(`Uploaded ${files.length} photo(s)`, { variant: 'success' });
    } catch (err) {
      addToast(parseApiError(err).formError || 'Upload failed', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (publicId) => {
    const ok = await confirm({
      title: 'Remove photo',
      message: 'Remove this photo? It is permanently deleted and cannot be recovered.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const updated = await removeProductPhoto(productId, publicId);
      onChanged?.(updated);
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not remove photo', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const move = async (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= ordered.length) return;
    const ids = ordered.map((p) => p.publicId);
    [ids[index], ids[next]] = [ids[next], ids[index]];
    setBusy(true);
    try {
      const updated = await reorderProductPhotos(productId, ids);
      onChanged?.(updated);
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not reorder photos', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ss-photos">
      {ordered.length > 0 ? (
        <ul className="ss-photo-grid">
          {ordered.map((p, i) => (
            <li key={p.publicId} className="ss-photo">
              <img src={cloudinaryThumb(p.url, 150)} srcSet={cloudinaryThumbSrcSet(p.url, 150)} alt="" loading="lazy" />
              {i === 0 && <span className="ss-photo__primary">Primary</span>}
              <div className="ss-photo__actions">
                <button type="button" className="ss-icon-btn" disabled={busy || i === 0} onClick={() => move(i, -1)} aria-label="Move earlier">
                  <ArrowUp size={15} aria-hidden="true" />
                </button>
                <button type="button" className="ss-icon-btn" disabled={busy || i === ordered.length - 1} onClick={() => move(i, 1)} aria-label="Move later">
                  <ArrowDown size={15} aria-hidden="true" />
                </button>
                <button type="button" className="ss-icon-btn ss-icon-btn--danger" disabled={busy} onClick={() => remove(p.publicId)} aria-label="Remove photo">
                  <Trash2 size={15} aria-hidden="true" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="ss-admin__lede">No photos yet — the storefront will show a branded placeholder.</p>
      )}

      {remaining > 0 ? (
        <ImageUploading multiple value={staged} onChange={setStaged} maxNumber={remaining} dataURLKey="dataURL">
          {({ imageList, onImageUpload, onImageRemove, isDragging, dragProps }) => (
            <div className="ss-uploader">
              <button
                type="button"
                className={`ss-dropzone ${isDragging ? 'is-dragging' : ''}`}
                onClick={onImageUpload}
                {...dragProps}
              >
                <ImagePlus size={20} aria-hidden="true" />
                <span>Click or drop images ({remaining} slot{remaining === 1 ? '' : 's'} left)</span>
              </button>

              {imageList.length > 0 && (
                <>
                  <ul className="ss-photo-grid ss-photo-grid--staged">
                    {imageList.map((img, index) => (
                      <li key={index} className="ss-photo ss-photo--staged">
                        <img src={img.dataURL} alt="" />
                        <div className="ss-photo__actions">
                          <button type="button" className="ss-icon-btn ss-icon-btn--danger" onClick={() => onImageRemove(index)} aria-label="Discard">
                            <Trash2 size={15} aria-hidden="true" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <Button size="sm" onClick={upload} disabled={busy}>
                    {busy ? <Spinner label="Uploading" /> : <><Upload size={16} aria-hidden="true" /> Upload {imageList.length} photo(s)</>}
                  </Button>
                </>
              )}
            </div>
          )}
        </ImageUploading>
      ) : (
        <p className="ss-admin__lede">Maximum of {MAX_PHOTOS} photos reached.</p>
      )}

      {dialog}
    </div>
  );
}
